// Billing status is verified with Stripe; is_active belongs to the saved
// entitlement row and cannot tell us whether a renewal was paid.
// A Free entitlement can retain an ended Stripe ID for history. An ID alone
// must not route that account to change-plan. Verified Stripe status wins if
// the saved Free row is stale while a billable subscription actually exists.
export const hasExistingBillingSubscription = subscription =>
  !!subscription?.billing?.verified &&
  !!(subscription.billing.id || subscription.current_stripe_subscription_id || subscription.stripe_subscription_id) &&
  ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'paused'].includes(subscription.billing.status);

// Even a saved Free row can be stale. Always ask the authoritative service.
export const shouldLoadBillingStatus = () => true;

export function reconcileBillingStatus(saved, result) {
  if (result?.verified !== true || !['free', 'basic', 'pro'].includes(result.current_plan)) {
    throw new Error('Billing status could not be verified.');
  }
  const billing = result.subscription;
  if ((result.current_plan === 'free') !== !billing || (billing && !billing.id)) {
    throw new Error('Billing status is inconsistent.');
  }
  return {
    ...saved,
    plan_name: result.current_plan,
    credit_tier: result.current_plan,
    is_active: billing ? ['active', 'trialing', 'past_due'].includes(billing.status) : true,
    current_stripe_subscription_id: billing?.id || null,
    billing: { ...(billing || { id: null, status: 'none' }), verified: true },
    trial: result.trial || { status: 'unverified' },
    next_billing_date: billing && !billing.cancel_at_period_end ? billing.period_end : null,
  };
}

export function paidPlanAction(subscription) {
  if (!subscription?.billing?.verified) return {
    kind: 'blocked', message: 'Could not confirm your current subscription. Return to Subscription Details and retry.',
  };
  const status = subscription.billing.status;
  if (['none', 'canceled', 'incomplete_expired'].includes(status)) return { kind: 'create' };
  if (['active', 'trialing'].includes(status)) return { kind: 'change' };
  return { kind: 'blocked', message: billingPresentation(subscription).message || 'Contact support to confirm your subscription before purchasing.' };
}

export const featuresForCadence = (plan, cadence) => plan.features.map(feature =>
  cadence === 'annual' && feature === 'Cancel anytime'
    ? '12-month commitment; cancellation available after the commitment ends' : feature);

export function billingPresentation(subscription) {
  if (subscription.plan_name === 'free' && subscription.billing?.verified) return { label: 'Free plan', active: false };
  const billing = subscription.billing;
  if (!billing?.verified) return {
    label: 'Subscription status needs confirmation', severity: 'warning',
    message: 'Could not confirm your current subscription with Stripe. Please retry or contact support.',
  };
  const status = billing.status;
  const active = ['active', 'trialing'].includes(status);
  const canceling = !!billing.cancel_at_period_end;
  const canCancel = ['active', 'trialing', 'past_due', 'unpaid', 'paused'].includes(status) && !billing.commitment?.locked;
  const messages = {
    past_due: 'Subscription payment failed. Update your payment card or pay the outstanding invoice, return to this page to check the updated status.',
    unpaid: 'Subscription payment remains unpaid. Update your card and resolve the outstanding invoice. Automatic retries may have ended.',
    incomplete: 'Your first subscription payment is incomplete. Complete the invoice payment before using this plan.',
    incomplete_expired: 'Your first subscription payment was not completed and the subscription expired.',
    canceled: 'Your subscription has ended. Paying an old invoice does not restart it. Subscribe again to resume renewals.',
    paused: 'Your subscription is paused. Contact support to confirm how to resume it.',
  };
  return { label: canceling && active ? 'Auto-renewal off' : ({ active: 'Active', trialing: 'Trialing', past_due: 'Payment overdue',
    unpaid: 'Unpaid', incomplete: 'Payment incomplete', incomplete_expired: 'Expired', canceled: 'Canceled', paused: 'Paused' }[status] || 'Subscription status needs confirmation'),
    active, canceling, canCancel, canChange: active || ['canceled', 'incomplete_expired'].includes(status),
    ended: ['canceled', 'incomplete_expired'].includes(status),
    severity: ['unpaid', 'incomplete_expired'].includes(status) ? 'error' : 'warning', message: messages[status] };
}


// Free signup and paid cancellation are separate operations. Historical IDs
// require a verified status before deciding which one is safe to send.
export function freePlanAction(subscription) {
  const id = subscription?.billing?.id || subscription?.current_stripe_subscription_id || subscription?.stripe_subscription_id;
  if (!id && (!subscription?.plan_name || subscription.plan_name === 'free')) return { kind: 'activate' };
  if (!subscription?.billing?.verified) return { kind: 'blocked', message: 'Return to Subscription Details and retry to confirm your subscription before switching to Free.' };
  const status = subscription.billing.status;
  if (['none', 'canceled', 'incomplete_expired'].includes(status)) return { kind: 'activate' };
  if (subscription.billing.commitment?.locked) return { kind: 'blocked', message: 'You cannot switch to Free during the 12-month annual commitment.' };
  if (id && ['active', 'trialing', 'past_due', 'unpaid', 'paused'].includes(status)) return { kind: 'cancel', id };
  return { kind: 'blocked', message: 'Resolve or confirm your subscription payment before switching to Free.' };
}
